import type { CompanionStatus } from '../companion/ScoutCompanion';
import { distance, type Vec3 } from './mathx';
import type { ScenarioSpec, ScenarioSuccess } from './scenarioSchema';
import type { SimState } from './Scenario';
import type { TargetState } from './targetTypes';
import type { Track } from './trackTypes';
import {
  SCOUT_CAMERA_CONFIG,
  isPointInFrustum,
  scoutCameraPose,
  type ScoutCameraState,
} from './vision';

export interface MetricsTickState {
  time: number;
  scout: ScoutCameraState;
  targets: readonly TargetState[];
}

export interface MetricValues {
  reactionTimeS: number;
  transitTimeS: number;
  stationErrorM: number;
  targetInFrustumPct: number;
  minSeparationM: number;
  trackContinuityPct: number;
}

export type ThresholdMetric = Exclude<keyof MetricValues, 'transitTimeS'>;
export type PerMetricOutcome = Record<ThresholdMetric, boolean>;

export interface MetricsResult extends MetricValues {
  outcome: 'pass' | 'fail';
  /** Transit has no Phase-1 scenario threshold, so it is intentionally absent. */
  perMetric: PerMetricOutcome;
}

export interface MetricsCameraConfig {
  fovDeg: number;
  aspect: number;
  near: number;
  far: number;
  tiltDeg: number;
}

/** Compare already-computed values with the five thresholds in a scenario. */
export const evaluateMetrics = (
  values: MetricValues,
  success: ScenarioSuccess,
): Pick<MetricsResult, 'outcome' | 'perMetric'> => {
  const perMetric: PerMetricOutcome = {
    reactionTimeS: values.reactionTimeS <= success.reactionTimeS,
    stationErrorM: values.stationErrorM <= success.stationErrorM,
    targetInFrustumPct: values.targetInFrustumPct >= success.targetInFrustumPct,
    minSeparationM: values.minSeparationM >= success.minSeparationM,
    trackContinuityPct: values.trackContinuityPct >= success.trackContinuityPct,
  };
  return {
    outcome: Object.values(perMetric).every(Boolean) ? 'pass' : 'fail',
    perMetric,
  };
};

const closestTargetId = (position: Vec3, targets: readonly TargetState[]): string | undefined => {
  let closest: TargetState | undefined;
  let closestDistance = Infinity;
  for (const target of targets) {
    const candidateDistance = distance(position, target.posEnu);
    if (candidateDistance < closestDistance) {
      closest = target;
      closestDistance = candidateDistance;
    }
  }
  return closest?.id;
};

/** Tick-fed, deterministic accumulator shared by headless runs and MCP episodes. */
export class MetricsAccumulator {
  private readonly camera: MetricsCameraConfig;
  private firstConfirmedTime: number | undefined;
  private leftPadTime: number | undefined;
  private firstOnStationTime: number | undefined;
  private selectedTargetId: string | undefined;
  private selectedTrackId: number | undefined;
  private continuityTicks = 0;
  private confirmedTicks = 0;
  private stationTicks = 0;
  private stationErrorSum = 0;
  private frustumHits = 0;
  private readonly minSeparationByTarget = new Map<string, number>();

  constructor(
    private readonly scenario: ScenarioSpec,
    camera: Partial<MetricsCameraConfig> = {},
  ) {
    this.camera = { ...SCOUT_CAMERA_CONFIG, ...camera };
  }

  addTick(state: SimState): void;
  addTick(
    state: MetricsTickState,
    tracks: readonly Track[],
    companionStatus: CompanionStatus,
  ): void;
  addTick(
    state: MetricsTickState & Partial<Pick<SimState, 'tracks' | 'companion'>>,
    tracks: readonly Track[] = state.tracks ?? [],
    companionStatus: CompanionStatus | undefined = state.companion,
  ): void {
    if (companionStatus === undefined) {
      throw new TypeError('MetricsAccumulator.addTick requires companion status');
    }

    this.updateSeparations(state);
    const confirmed = tracks.filter((track) => track.state === 'confirmed');
    const selectedTrack = tracks.find((track) => track.id === companionStatus.selectedTrackId);

    if (this.firstConfirmedTime === undefined && confirmed.length > 0) {
      this.firstConfirmedTime = state.time;
      const first =
        confirmed.find((track) => track.id === companionStatus.selectedTrackId) ?? confirmed[0];
      this.selectedTrackId = first?.id;
      this.selectedTargetId = this.targetIdForTrack(first, companionStatus, state.targets);
    }

    if (this.selectedTargetId === undefined && selectedTrack !== undefined) {
      this.selectedTrackId = selectedTrack.id;
      this.selectedTargetId = this.targetIdForTrack(selectedTrack, companionStatus, state.targets);
    }

    if (
      this.leftPadTime === undefined &&
      distance(state.scout.posEnu, this.scenario.scout.startEnu) > 2
    ) {
      this.leftPadTime = state.time;
    }
    if (this.firstOnStationTime === undefined && companionStatus.state === 'ON_STATION') {
      this.firstOnStationTime = state.time;
    }

    if (this.firstConfirmedTime !== undefined) {
      this.continuityTicks += 1;
      if (this.hasConfirmedSelectedTrack(confirmed)) this.confirmedTicks += 1;
    }

    const target = this.selectedTarget(state.targets);
    if (companionStatus.state === 'ON_STATION' && target !== undefined) {
      const horizontalDistance = Math.hypot(
        state.scout.posEnu.x - target.posEnu.x,
        state.scout.posEnu.y - target.posEnu.y,
      );
      const radialError = Math.abs(horizontalDistance - companionStatus.effectiveRadius);
      const altitudeError = Math.abs(state.scout.posEnu.z - this.scenario.companion.observeAlt);
      this.stationTicks += 1;
      this.stationErrorSum += Math.hypot(radialError, altitudeError);
      const pose = scoutCameraPose(state.scout, this.camera.tiltDeg);
      if (
        isPointInFrustum(
          pose,
          this.camera.fovDeg,
          this.camera.aspect,
          this.camera.near,
          this.camera.far,
          target.posEnu,
        )
      ) {
        this.frustumHits += 1;
      }
    }
  }

  result(): MetricsResult {
    const reactionTimeS =
      this.firstConfirmedTime === undefined || this.leftPadTime === undefined
        ? Infinity
        : Math.max(0, this.leftPadTime - this.firstConfirmedTime);
    const transitTimeS =
      this.leftPadTime === undefined || this.firstOnStationTime === undefined
        ? Infinity
        : Math.max(0, this.firstOnStationTime - this.leftPadTime);
    const values: MetricValues = {
      reactionTimeS,
      transitTimeS,
      stationErrorM: this.stationTicks === 0 ? Infinity : this.stationErrorSum / this.stationTicks,
      targetInFrustumPct:
        this.stationTicks === 0 ? 0 : (100 * this.frustumHits) / this.stationTicks,
      minSeparationM:
        this.selectedTargetId === undefined
          ? 0
          : (this.minSeparationByTarget.get(this.selectedTargetId) ?? 0),
      trackContinuityPct:
        this.continuityTicks === 0 ? 0 : (100 * this.confirmedTicks) / this.continuityTicks,
    };
    return { ...values, ...evaluateMetrics(values, this.scenario.success) };
  }

  private updateSeparations(state: MetricsTickState): void {
    for (const target of state.targets) {
      const separation = distance(state.scout.posEnu, target.posEnu);
      const previous = this.minSeparationByTarget.get(target.id) ?? Infinity;
      this.minSeparationByTarget.set(target.id, Math.min(previous, separation));
    }
  }

  private targetIdForTrack(
    track: Track | undefined,
    companionStatus: CompanionStatus,
    targets: readonly TargetState[],
  ): string | undefined {
    if (track?.assocTruthId !== undefined) return track.assocTruthId;
    if (companionStatus.targetPosition !== null) {
      return closestTargetId(companionStatus.targetPosition, targets);
    }
    return targets.length === 1 ? targets[0]?.id : undefined;
  }

  private hasConfirmedSelectedTrack(confirmed: readonly Track[]): boolean {
    return confirmed.some((track) =>
      this.selectedTargetId === undefined
        ? track.id === this.selectedTrackId
        : track.assocTruthId === this.selectedTargetId ||
          (track.assocTruthId === undefined && track.id === this.selectedTrackId),
    );
  }

  private selectedTarget(targets: readonly TargetState[]): TargetState | undefined {
    return targets.find((target) => target.id === this.selectedTargetId);
  }
}
